import { useState, useEffect } from "react";
import {
  Printer,
  Cpu,
  LayoutGrid,
  Bot,
  Plane,
  Car,
  Boxes,
  Rocket,
  ShieldCheck,
  Zap,
  Sparkles,
  Calendar,
  Battery,
  Trophy,
  Wrench,
  Award,
  Layers,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";


// Slide 0: original Hero machine grid
const machines = [
  { label: "3D Print", icon: Printer, bg: "from-sky-400 to-blue-500" },
  { label: "CNC", icon: Cpu, bg: "from-indigo-400 to-indigo-600" },
  { label: "PCB", icon: LayoutGrid, bg: "from-sky-400 to-cyan-500" },
  { label: "Robotics", icon: Bot, bg: "from-pink-400 to-fuchsia-600" },
  { label: "Drone", icon: Plane, bg: "from-blue-400 to-indigo-600" },
  { label: "EV", icon: Car, bg: "from-orange-400 to-rose-500" },
  { label: "CAD", icon: Boxes, bg: "from-fuchsia-400 to-purple-600" },
  { label: "Launch", icon: Rocket, bg: "from-amber-400 to-orange-500" },
];

// Slides 1-4: event highlights, each with its own themed icon grid
const eventSlides = [
  {
    tag: "Bootcamp",
    title: "Advanced 3D Printing Bootcamp",
    date: "Jan 12–16, 2026",
    location: "APIC Vijayawada",
    description:
      "Hands-on with industrial FDM, SLA and metal printing. Certification included.",
    bg: "from-[#0c4a6e] via-[#0369a1] to-[#4c1d95]",
    href: "/events/3d-printing-bootcamp",
    icons: [
      { label: "FDM", icon: Printer, bg: "from-sky-400 to-blue-500" },
      { label: "Layers", icon: Layers, bg: "from-cyan-400 to-sky-500" },
      { label: "Models", icon: Boxes, bg: "from-blue-400 to-indigo-600" },
      { label: "Certified", icon: ShieldCheck, bg: "from-indigo-400 to-indigo-600" },
    ],
  },
  {
    tag: "Hackathon",
    title: "EV & Mobility Prototype Hackathon",
    date: "Dec 05–07, 2025",
    location: "APIC Tirupati",
    description:
      "48-hour hardware sprint with ₹15L in prizes, mentorship and industry partners.",
    bg: "from-[#4c1d95] via-[#a21caf] to-[#c2410c]",
    href: "/events/ev-mobility-hackathon",
    icons: [
      { label: "EV", icon: Car, bg: "from-orange-400 to-rose-500" },
      { label: "Battery", icon: Battery, bg: "from-fuchsia-400 to-purple-600" },
      { label: "Sprint", icon: Zap, bg: "from-amber-400 to-orange-500" },
      { label: "Prizes", icon: Trophy, bg: "from-pink-400 to-fuchsia-600" },
    ],
  },
  {
    tag: "Workshop",
    title: "PCB Design & Fabrication Workshop",
    date: "Feb 18–20, 2026",
    location: "APIC Visakhapatnam",
    description:
      "From schematic to soldered board — layout, etching and reflow, taught by industry engineers.",
    bg: "from-[#064e3b] via-[#0f766e] to-[#0e7490]",
    href: "/events/pcb-workshop",
    icons: [
      { label: "Layout", icon: LayoutGrid, bg: "from-teal-400 to-emerald-500" },
      { label: "Fab", icon: Cpu, bg: "from-cyan-400 to-teal-600" },
      { label: "Tools", icon: Wrench, bg: "from-emerald-400 to-green-600" },
      { label: "Quality", icon: ShieldCheck, bg: "from-sky-400 to-cyan-600" },
    ],
  },
  {
    tag: "Demo Day",
    title: "Robotics & Automation Demo Day",
    date: "Mar 02, 2026",
    location: "APIC Amaravati",
    description:
      "Startups and student teams showcase working robotics prototypes to investors and industry.",
    bg: "from-[#831843] via-[#a21caf] to-[#4c1d95]",
    href: "/events/robotics-demo-day",
    icons: [
      { label: "Robotics", icon: Bot, bg: "from-pink-400 to-fuchsia-600" },
      { label: "Launch", icon: Rocket, bg: "from-amber-400 to-orange-500" },
      { label: "Teams", icon: Users, bg: "from-fuchsia-400 to-purple-600" },
      { label: "Awards", icon: Award, bg: "from-rose-400 to-pink-600" },
    ],
  },
];

const TOTAL_SLIDES = eventSlides.length + 1; // +1 for the Hero slide
const AUTO_SCROLL_MS = 6000;

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % TOTAL_SLIDES);
    }, AUTO_SCROLL_MS);
    return () => clearInterval(timer);
  }, [isPaused]);

  const goTo = (i) => setIndex(((i % TOTAL_SLIDES) + TOTAL_SLIDES) % TOTAL_SLIDES);

  return (
    <section
      className="relative overflow-hidden text-white"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Sliding track */}
      <div
        className="flex transition-transform duration-700 ease-in-out"
        style={{
          width: `${TOTAL_SLIDES * 100}%`,
          transform: `translateX(-${(index * 100) / TOTAL_SLIDES}%)`,
        }}
      >
        {/* Slide 0: original Hero */}
        <div
          className="bg-gradient-to-br from-[#1e1b4b] via-[#312e81] to-[#4c1d95]"
          style={{ width: `${100 / TOTAL_SLIDES}%` }}
        >
          <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
            {/* Left column */}
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-medium bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6">
                <Sparkles className="w-3.5 h-3.5" />
                Government of Andhra Pradesh · Innovation Ecosystem
              </span>

              <h1 className="text-5xl md:text-6xl font-extrabold leading-tight mb-6">
                Turn Your Ideas <br />
                <span className="bg-gradient-to-r from-teal-300 via-yellow-200 to-orange-400 bg-clip-text text-transparent">
                  Into Reality.
                </span>
              </h1>

              <p className="text-indigo-100 text-lg max-w-xl mb-8">
                Access advanced prototyping infrastructure, machines, experts
                and innovation facilities across Andhra Pradesh — all through
                one digital platform.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <Link
                  to="/register"
                  className="flex items-center gap-2 bg-white text-indigo-900 font-semibold px-6 py-3 rounded-xl hover:bg-gray-100 transition"
                >
                  Start Your Prototype →
                </Link>

                <Link
                  to="/Facilitieshomepage"
                  className="flex items-center gap-2 border border-white/30 font-semibold px-6 py-3 rounded-xl hover:bg-white/10 transition"
                >
                  Explore APIC Facilities
                </Link>
              </div>

              <div className="flex flex-wrap gap-6 text-sm text-indigo-200">
                <span className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" /> ISO 9001 Certified Centres
                </span>
                <span className="flex items-center gap-2">
                  <Zap className="w-4 h-4" /> 24hr Quotation Turnaround
                </span>
              </div>
            </div>

            {/* Right column - machine grid */}
            <div className="relative">
              <div className="grid grid-cols-3 gap-4">
                {machines.map(({ label, icon: Icon, bg }) => (
                  <div
                    key={label}
                    className="bg-white/10 border border-white/10 rounded-2xl p-5 flex flex-col items-center justify-center gap-3 hover:bg-white/15 transition"
                  >
                    <div
                      className={`w-10 h-10 rounded-full bg-gradient-to-br ${bg} flex items-center justify-center`}
                    >
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-sm font-medium">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Slides 1-4: event highlights, each with a themed icon grid on the right */}
        {eventSlides.map((slide) => (
          <div
            key={slide.tag}
            className={`bg-gradient-to-br ${slide.bg}`}
            style={{ width: `${100 / TOTAL_SLIDES}%` }}
          >
            <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center min-h-[420px]">
              {/* Left column - event details */}
              <div>
                <span className="inline-flex w-fit items-center gap-2 text-xs font-semibold bg-white/15 border border-white/25 rounded-full px-4 py-1.5 mb-6">
                  <Sparkles className="w-3.5 h-3.5" />
                  {slide.tag}
                </span>

                <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-6">
                  {slide.title}
                </h1>

                <div className="flex items-center gap-2 text-indigo-100 text-lg mb-4">
                  <Calendar className="w-5 h-5" />
                  {slide.date} · {slide.location}
                </div>

                <p className="text-indigo-100 text-lg max-w-xl mb-8">
                  {slide.description}
                </p>

                <div className="flex flex-wrap gap-4">
                

<Link
  to="/register"
  className="flex items-center gap-2 bg-white text-indigo-900 font-semibold px-6 py-3 rounded-xl hover:bg-gray-100 transition"
>
  Register →
</Link>
                  
                </div>
              </div>

              {/* Right column - themed icon grid */}
              <div className="relative">
                <div className="grid grid-cols-2 gap-4">
                  {slide.icons.map(({ label, icon: Icon, bg }) => (
                    <div
                      key={label}
                      className="bg-white/10 border border-white/10 rounded-2xl p-5 flex flex-col items-center justify-center gap-3 hover:bg-white/15 transition"
                    >
                      <div
                        className={`w-10 h-10 rounded-full bg-gradient-to-br ${bg} flex items-center justify-center`}
                      >
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-sm font-medium">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {Array.from({ length: TOTAL_SLIDES }).map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-2 rounded-full transition-all duration-300 ${i === index ? "w-6 bg-white" : "w-2 bg-white/40 hover:bg-white/60"
              }`}
          />
        ))}
      </div>
    </section>
  );
}